import { useEffect, useState } from "react";
import styles from "../list.module.css";
import { Select, FormControl, InputLabel, MenuItem } from "@mui/material";
import { ArrowLeft, ArrowRight, Filter } from "react-feather";
import { DirectionsRenderer, GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";

const colors = ["#F9B12C", "#F58030", "#DB1F26", "#E31B4D", "#39A0D9", "#383E98", "#7CC245", "#4B362A", "#86459A", "#D7D95E", "#89A63D", "#FED700"];

const getHomeIconUrl = () => {
    const svgString = `
    <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 47.17 53.46"><defs><style>.cls-1{fill:#fff;stroke:#4b362a;stroke-miterlimit:10;stroke-width:3px;}.cls-2{fill:#4b362a;}</style></defs><path class="cls-1" d="M39.47,4.77H10.53a7.62,7.62,0,0,0-7.62,7.62V36a7.62,7.62,0,0,0,7.62,7.62h5.79L22,53.5a3.45,3.45,0,0,0,6,0l5.69-9.85h5.79A7.62,7.62,0,0,0,47.09,36V12.39A7.62,7.62,0,0,0,39.47,4.77Z" transform="translate(-1.41 -3.27)"/><path class="cls-2" d="M22.77,34.27H19.21a2.09,2.09,0,0,1-1.75-1.7,1.83,1.83,0,0,1,0-.33V26.77c0-.27,0-.28-.27-.27a1.79,1.79,0,0,1-1.77-1.77,1.93,1.93,0,0,1,.59-1.4c2.57-2.56,5.14-5.11,7.68-7.7a2,2,0,0,1,2.69,0c2.53,2.57,5.09,5.11,7.64,7.66a1.76,1.76,0,0,1,.6,1.14V25a1.76,1.76,0,0,1-1.46,1.5c-.11,0-.22,0-.33,0s-.21,0-.21.22a.86.86,0,0,0,0,.16v5.32a2.12,2.12,0,0,1-1.33,2,2.81,2.81,0,0,1-.49.09H27.26c-.35-.11-.47-.29-.47-.7V29.29a1,1,0,0,0-.37-.8,1.13,1.13,0,0,0-.64-.18H24.25a1.09,1.09,0,0,0-.61.16.9.9,0,0,0-.4.8v4.37A.59.59,0,0,1,22.77,34.27Z" transform="translate(-1.41 -3.27)"/></svg>
    `;

    return "data:image/svg+xml;base64," + btoa(svgString);
}

const getIconUrl = (color, index) => {
    const svgString = `
    <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 38 42.98"><defs><style>.cls-1{fill:#fff;stroke: ${color};stroke-miterlimit:10;stroke-width:3px;}.cls-2{font-size:18px;fill: ${color};font-family:Poppins-SemiBold, Poppins;font-weight:600;}</style></defs><path class="cls-1" d="M36.46,10H13.54a6,6,0,0,0-6,6V34.78a6,6,0,0,0,6,6h4.58l4.51,7.8a2.73,2.73,0,0,0,4.74,0l4.51-7.8h4.58a6,6,0,0,0,6-6V16.05A6,6,0,0,0,36.46,10Z" transform="translate(-6 -8.51)"/><text class="cls-2" transform="translate(16.04 24.01)">${index}</text></svg>
    `;

    return "data:image/svg+xml;base64," + btoa(svgString);
}

const lightenColor = (hex, percent) => {
    const num = parseInt(hex?.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;

    return ("#" + (0x1000000 + (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 + (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 + (B < 255 ? (B < 1 ? 0 : B) : 255)).toString(16).slice(1));
}

const Step2 = ({ routes, setCurrentStep }) => {
    const { isLoaded } = useLoadScript({
        id: "google-map-script",
        googleMapsApiKey: process.env.REACT_APP_MAP_API_KEY,
        libraries: ["places"],
    });
    const [allDirections, setAllDirections] = useState([]);
    const [directions, setDirections] = useState([]);
    const [driverList, setDriverList] = useState([]);
    const [selectedDriver, setSelectedDriver] = useState("all");
    const MAX_WAYPOINTS = 20;

    const totalStops = routes?.reduce((sum, r) => sum + (r?.bookings?.length || 0), 0) || 0;
    const driverCount = routes?.length || 0;

    useEffect(() => {
        if (isLoaded && routes && routes.length > 0) {
            let driverListTemp = routes?.map(i => {
                return i?.driver;
            });

            setDriverList(driverListTemp);

            const service = new window.google.maps.DirectionsService();

            routes.forEach((route, idx) => {
                const bookings = route.bookings;
                if (!bookings || bookings.length < 1) return;

                const driverLoc = {
                    lat: parseFloat(route.driver.lat),
                    lng: parseFloat(route.driver.lng),
                };

                // Split into multiple chunks if necessary
                const chunks = [];
                for (let i = 0; i < bookings.length; i += MAX_WAYPOINTS) {
                    chunks.push(bookings.slice(i, i + MAX_WAYPOINTS + 1));
                }

                chunks.forEach((chunk, chunkIdx) => {
                    const origin = chunkIdx === 0 ? driverLoc : { lat: parseFloat(chunk[0].lat), lng: parseFloat(chunk[0].lng) };

                    const destination = { lat: parseFloat(chunk[chunk.length - 1].lat), lng: parseFloat(chunk[chunk.length - 1].lng) };

                    const waypoints = chunk.slice(0, -1).map((stop) => ({
                        location: new window.google.maps.LatLng(parseFloat(stop.lat), parseFloat(stop.lng)),
                        stopover: true,
                    }));

                    const options = { origin, destination, waypoints, travelMode: window.google.maps.TravelMode.DRIVING };

                    service.route(options, (result, status) => {
                        if (status === "OK" && result) {
                            setAllDirections((prev) => [...prev, { idx: chunkIdx, result, driver: route.driver.id }]);
                        }
                    });
                });
            });
        }
    }, [isLoaded, routes]);

    useEffect(() => {
        let directionsTemp = allDirections;

        if (selectedDriver !== "" && selectedDriver !== "all") {
            directionsTemp = directionsTemp?.filter((i) => i?.driver === selectedDriver);
        }

        setDirections(directionsTemp);
    }, [allDirections, selectedDriver]);

    return (<div>
        <div className={`${styles.Step2TopBar}`}>
            <button className={`${styles.Step3BackBU}`} onClick={(e) => setCurrentStep(1)} aria-label="Back to bookings">
                <ArrowLeft size={18} />
            </button>

            <div className={`${styles.Step2HeadingWrap}`}>
                <p className={`${styles.Step3Title}`}>Review routes</p>
                <p className={`${styles.Step3Subtitle}`}><span className={styles.Mono}>{driverCount}</span> driver{driverCount === 1 ? "" : "s"} · <span className={styles.Mono}>{totalStops}</span> stop{totalStops === 1 ? "" : "s"}</p>
            </div>

            <div className={`${styles.Step2FilterWrap} Select`}>
                <FormControl fullWidth size="small">
                    <InputLabel><Filter size={13} style={{ verticalAlign: "-2px", marginRight: 4 }} />Filter By Driver</InputLabel>
                    <Select
                        label="Filter By Driver"
                        value={selectedDriver}
                        onChange={(e) => {
                            setSelectedDriver(e.target.value);
                        }}
                    >
                        <MenuItem value="all">All Drivers</MenuItem>
                        {driverList?.map((i) => {
                            return <MenuItem key={i?.id} value={i?.id}>{i?.name}</MenuItem>;
                        })}
                    </Select>
                </FormControl>
            </div>

            <button className={`${styles.Step2NextBU}`} onClick={(e) => setCurrentStep(3)}>
                Next &nbsp; <ArrowRight size={18} />
            </button>
        </div>

        {isLoaded && (<GoogleMap
            center={{ lat: 51.509865, lng: -0.118092 }}
            zoom={12}
            mapContainerClassName={styles.MapHeightResponsive}
        >
            {directions.map((d, i) => {
                let selColor = colors[d?.driver % colors.length];
                return (<div key={i}>
                    <DirectionsRenderer
                        directions={d.result}
                        options={{
                            suppressMarkers: true,
                            polylineOptions: {
                                strokeColor: selColor,
                                strokeOpacity: 0.9,
                                strokeWeight: 8,
                                zIndex: 1
                            }
                        }}
                    />

                    <DirectionsRenderer
                        directions={d.result}
                        options={{
                            suppressMarkers: true,
                            polylineOptions: {
                                strokeColor: lightenColor(selColor, 50),
                                strokeOpacity: 1,
                                strokeWeight: 4,
                                zIndex: 2
                            }
                        }}
                    />

                    {d.result.routes[0].legs.map((leg, legIndex) => {
                        return (<div key={legIndex}>
                            {legIndex === 0 && d?.idx === 0 && (<Marker
                                position={leg.start_location}
                                icon={{
                                    url: getHomeIconUrl(),
                                    scaledSize: new window.google.maps.Size(40, 50),
                                }}
                                zIndex={500}
                            />)}

                            <Marker
                                position={leg.end_location}
                                icon={{
                                    url: getIconUrl(selColor, MAX_WAYPOINTS * d?.idx + legIndex + 1),
                                    scaledSize: new window.google.maps.Size(35, 40),
                                }}
                                zIndex={500 - legIndex}
                            />
                        </div>);
                    })}
                </div>);
            })}
        </GoogleMap>)}
    </div>);
};

export default Step2;